<template>
  <div
    ref="chartdiv"
    class="hello"
    :class="typeDisplay"
  ></div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

export default {
  props: {
    chartData: {
      type: Array,
      default() {
        return [];
      },
    },

    typeDisplay: {
      type: String,
      default: 'normal',
    },
  },

  data() {
    return {
      chart: null,
      formatted: [],
    };
  },

  computed: {
    fontColor() {
      return getComputedStyle(document.getElementById('app')).getPropertyValue('--themeFontColor');
    },
  },

  watch: {
    chartData() {
      // If chart data changed, redo chart render
      this.chart.dispose();
      this.formattedDate();
    },
  },

  mounted() {
    this.formattedDate();
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },

  methods: {
    formattedDate() {
      const data = [];
      const localChartData = this.chartData;

      if (!localChartData.length) {
        data.push({
          lga1: null,
          lga2: null,
          title: null,
        });
      } else {
        localChartData.forEach((el) => {
          data.push({
            lga1: Math.round(el.lga1.total_avg * 10000) / 100,
            lga2: Math.round(el.lga2.total_avg * 10000) / 100,
            title: el.session_id.split('_')[1], // ex: el.session_id => 'session_5'
          });
        });

        data.sort((a, b) => a.title - b.title);
      }

      this.formatted = data;
      this.chartStart();
    },

    chartStart() {
      am4core.addLicense(process.env.VUE_APP_AMCHARTS_LICENSE);
      am4core.useTheme(am4themesAnimated);

      // Create chart instance
      this.chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);

      this.chart.data = this.formatted;

      // Add chart background color
      this.chart.plotContainer.background.fill = am4core.color(this.fontColor);
      this.chart.plotContainer.background.fillOpacity = 0.1;

      // Create X axes
      const categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
      // // Label
      categoryAxis.dataFields.category = 'title';
      categoryAxis.renderer.labels.template.fill = am4core.color(this.fontColor);
      // // Grid
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 20;
      categoryAxis.renderer.grid.template.stroke = am4core.color(this.fontColor);
      categoryAxis.renderer.grid.template.strokeOpacity = 0.5;
      categoryAxis.renderer.grid.template.strokeDasharray = 5;
      // // Cell
      categoryAxis.renderer.cellStartLocation = 0.1;
      categoryAxis.renderer.cellEndLocation = 0.9;
      // // Stroke
      categoryAxis.renderer.line.strokeWidth = 2;
      categoryAxis.renderer.line.strokeOpacity = 1;
      categoryAxis.renderer.line.stroke = am4core.color(this.fontColor);

      categoryAxis.title.text = this.$t('actionPlan.session');
      categoryAxis.title.fontWeight = 'bold';
      categoryAxis.title.fill = am4core.color(this.fontColor);

      const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
      // // Label
      valueAxis.renderer.labels.template.fill = am4core.color(this.fontColor);
      // // Grid
      valueAxis.min = 0;
      valueAxis.max = 105;
      valueAxis.strictMinMax = true;

      valueAxis.title.text = `${this.$t('actionPlan.completionRate')} (%)`;
      valueAxis.title.fontWeight = 'bold';
      valueAxis.title.fill = am4core.color(this.fontColor);

      valueAxis.renderer.grid.template.stroke = am4core.color(this.fontColor);
      valueAxis.renderer.grid.template.strokeOpacity = 0.5;
      valueAxis.renderer.minGridDistance = 100;
      // // Stroke
      valueAxis.renderer.line.strokeWidth = 2;
      valueAxis.renderer.line.strokeOpacity = 1;
      valueAxis.renderer.line.stroke = am4core.color(this.fontColor);
      // valueAxis.renderer.ticks.template.length = 5;

      this.createLineSeries('lga1', 'LGA 1', '#09eacf');
      this.createLineSeries('lga2', 'LGA 2', '#dfe500');

      // Add legend
      if (this.typeDisplay === 'normal') {
        this.chart.legend = new am4charts.Legend();
        this.chart.legend.labels.template.fill = am4core.color(this.fontColor);
        this.chart.legend.position = 'top';
        this.chart.legend.contentAlign = 'right';
        this.chart.legend.itemContainers.template.stroke = am4core.color(this.fontColor);
        this.chart.legend.itemContainers.template.strokeOpacity = 0.8;
        this.chart.legend.itemContainers.template.strokeWidth = 2;
        this.chart.legend.itemContainers.template.paddingLeft = 10;
        this.chart.legend.itemContainers.template.paddingRight = 10;
        this.chart.legend.itemContainers.template.paddingBottom = 5;
        this.chart.legend.itemContainers.template.paddingTop = 7;
        this.chart.legend.itemContainers.template.marginBottom = 20;
        this.chart.legend.itemContainers.template.marginRight = 0;
        this.chart.legend.itemContainers.template.marginLeft = 20;

        this.chart.legend.labels.template.strokeWidth = 0;

        // Custom disable style
        const as = this.chart.legend.itemContainers.template.states.getKey('active');
        as.properties.strokeOpacity = 0.2;
      }
    },
    // Create series
    createLineSeries(field, name, color) {
      const lineSeries = this.chart.series.push(new am4charts.LineSeries());

      lineSeries.name = name;
      lineSeries.dataFields.valueY = field;
      lineSeries.dataFields.categoryX = 'title';

      lineSeries.stroke = am4core.color(color);
      lineSeries.strokeWidth = 2;
      lineSeries.propertyFields.strokeDasharray = 'lineDash';
      lineSeries.tooltip.label.textAlign = 'middle';
      lineSeries.tooltip.background.strokeOpacity = 0;
      lineSeries.tooltip.getFillFromObject = false;
      lineSeries.tooltip.background.fill = am4core.color('white');
      lineSeries.tooltip.label.fill = am4core.color('black');

      const bullet = lineSeries.bullets.push(new am4charts.Bullet());
      bullet.fill = am4core.color(color); // tooltips grab fill from parent by default
      bullet.tooltipText = '[#696969 font-size: 15px]{name}\n[/][#696969 font-size: 20px]{valueY}[/]';

      const circle = bullet.createChild(am4core.Circle);
      circle.radius = 4;
      circle.fill = am4core.color(color);
      circle.strokeWidth = 3;
    },
  },
};
</script>

<style lang="scss" scoped>
.hello {
  width: calc(100% + 10px);
  margin-bottom: 20px;
  font-size: 12px;
  font-style: normal;

  &.normal {
    height: 390px;
    width: calc(100% + 42px);
  }

  &.small {
    height: 290px;
  }
}
</style>
